import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X, Settings as SettingsIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { useAuth } from '../services/api';

/**
 * Persistent banner shown at the top of the app whenever the last attempted
 * outgoing email failed. Auto-refreshes every 60s and after each route change.
 * Visible to admins and gestionnaires only — regular users don't have SMTP rights.
 */
export function SmtpHealthBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);

  const canSeeAlert = user?.role === 'admin' || user?.role === 'gestionnaire';

  const fetchHealth = useCallback(async () => {
    if (!canSeeAlert) return;
    try {
      const r = await api.get('/smtp/health');
      setHealth(r.data);
    } catch (_) {
      // Silent
    }
  }, [canSeeAlert]);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60_000); // refresh every minute
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const dismiss = async () => {
    try {
      await api.post('/smtp/health/dismiss');
      setHealth({ ok: true });
    } catch (_) {
      // Silent
    }
  };

  if (!canSeeAlert || !health || health.ok !== false) return null;

  const goToSettings = () => navigate('/settings');

  return (
    <div
      role="alert"
      data-testid="smtp-health-banner"
      className="ml-64 sticky top-0 z-40 bg-red-50 border-b border-red-200 px-6 py-3"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-800">
            Le dernier envoi d'email a échoué
          </p>
          <p className="text-sm text-red-700 mt-0.5">
            {health.user_message}
          </p>
          {health.recipient && (
            <p className="text-xs text-red-600 mt-1">
              Destinataire concerné : <span className="font-mono">{health.recipient}</span>
              {health.timestamp && ` — ${new Date(health.timestamp).toLocaleString('fr-FR')}`}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={goToSettings}
              className="inline-flex items-center gap-1 text-xs font-medium text-red-700 underline hover:text-red-900"
              data-testid="smtp-health-banner-settings-btn"
            >
              <SettingsIcon className="h-3.5 w-3.5" />
              Vérifier la configuration SMTP
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer l'alerte SMTP"
          data-testid="smtp-health-banner-dismiss-btn"
          className="text-red-400 hover:text-red-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
